import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api.js';
import { LivestockDetailSheet } from './LivestockDetailSheet.jsx';
import { localDateIso } from '../local-date.js';
import {
  catalogEmptyMessage,
  catalogFits,
  customSpeciesFields,
  customSpeciesHint,
} from '../saltwater-tools.js';
import { AutoVideo } from './AutoVideo.jsx';
import { formatVolume, getDisplayUnits } from '../units.js';
import { catalogFieldLabel, catalogValueShown } from '../shown-units.js';
import {
  MEDIA_ACCEPT,
  VIDEO_MAX_BYTES,
  looksLikeImage,
  looksLikeVideo,
  uploadResidentMedia,
} from '../livestock-media.js';

// Browse the saltwater catalog with image cards + filter chips. Replaces
// the search-by-name flow as the discovery entry point (the legacy
// search is still inside TankSettingsSheet for users who know exactly
// what they want).
//
// UX choices:
//   - Image-first cards (Wikimedia thumbnails, already in catalog rows)
//   - Verdict-coloured ring around each card so the user sees "fits my
//     tank" without opening the detail
//   - "Fits my tank" is the default filter ON — beginners shouldn't
//     have to learn the rules to see what's appropriate
//   - Hard NOs aren't hidden when fits-only is off, so the keeper can
//     learn WHY something doesn't fit (educational)
//   - Search bar at the top works alongside the chips
//   - Loading shimmer cards on first paint
//   - Empty state explains WHY (e.g. cycling phase)

const KIND_TABS = [
  { k: '', label: 'All' },
  { k: 'fish', label: 'Fish' },
  { k: 'coral', label: 'Coral' },
  { k: 'invert', label: 'Inverts' },
];

const VERDICT_RING = {
  good: 'var(--ok, #4ade80)',
  caution: 'var(--cyan)',
  no: 'var(--alert)',
};

// Category chips by kind. Mirrors the options in KIND_EXTRAS_SCHEMA so
// every value the user can pick on the custom-add form lines up with the
// values seeded in livestock_catalog.category. Fish have no category.
const CATEGORY_OPTIONS = {
  coral: ['SPS', 'LPS', 'soft', 'zoa', 'mushroom', 'gorgonian'],
  invert: ['snail', 'shrimp', 'crab', 'star', 'urchin', 'clam', 'anemone', 'worm'],
};

// Per-kind extra fields for the custom-add form, named like the catalog's.
// Stored only as lines at the top of the resident's `notes`: the fit rules
// and Sensei's species advice read catalog rows, never these (feedback #73).
const KIND_EXTRAS_SCHEMA = {
  fish: [
    { key: 'care_level', label: 'Care level', options: ['beginner', 'intermediate', 'expert'] },
    {
      key: 'temperament',
      label: 'Temperament',
      options: ['peaceful', 'semi-aggressive', 'aggressive'],
    },
    { key: 'reef_safe', label: 'Reef-safe', options: ['yes', 'with caution', 'no'] },
    { key: 'min_tank_gal', label: 'Min tank', type: 'number', unitKind: 'volume' },
  ],
  coral: [
    {
      key: 'category',
      label: 'Type',
      options: ['SPS', 'LPS', 'soft', 'zoa', 'mushroom', 'gorgonian', 'other'],
    },
    { key: 'care_level', label: 'Care level', options: ['beginner', 'intermediate', 'expert'] },
    { key: 'light_pref', label: 'Light', options: ['low', 'medium', 'high'] },
    { key: 'flow_pref', label: 'Flow', options: ['low', 'medium', 'high'] },
    { key: 'placement', label: 'Placement', options: ['bottom', 'mid', 'top'] },
  ],
  invert: [
    {
      key: 'category',
      label: 'Type',
      options: ['snail', 'shrimp', 'crab', 'star', 'urchin', 'clam', 'anemone', 'worm', 'other'],
    },
    { key: 'care_level', label: 'Care level', options: ['beginner', 'intermediate', 'expert'] },
    { key: 'reef_safe', label: 'Reef-safe', options: ['yes', 'with caution', 'no'] },
  ],
};

function formatExtrasBlock(extras, kind, units) {
  const schema = KIND_EXTRAS_SCHEMA[kind] || [];
  const lines = [];
  for (const field of schema) {
    const v = (extras[field.key] || '').toString().trim();
    if (!v) continue;
    lines.push(`• ${catalogFieldLabel(field.label, field.unitKind, units)}: ${v}`);
  }
  return lines.join('\n');
}

function dayLabel(iso) {
  const d = new Date(`${iso}T12:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export function LivestockBrowser({ tank, onClose, onReload, backLabel = 'Home' }) {
  const [kind, setKind] = useState('');
  const [category, setCategory] = useState('');
  const [fitsOnly, setFitsOnly] = useState(true);
  const [beginnerOnly, setBeginnerOnly] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [phase, setPhase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [selected, setSelected] = useState(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [addedIds, setAddedIds] = useState(() => new Set());
  const [addingId, setAddingId] = useState(null);
  const [quickErr, setQuickErr] = useState('');
  const [quickMsg, setQuickMsg] = useState('');
  // One date for everything added from this page: a keeper logging corals
  // they got last week sets it once, then adds them with a tap each.
  const [addedOn, setAddedOn] = useState(localDateIso);

  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const addedTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
      if (addedTimerRef.current) clearTimeout(addedTimerRef.current);
    };
  }, []);

  const fetchResults = useMemo(
    () => async (kindFilter, catFilter, fits, beginner, term, signal) => {
      const params = new URLSearchParams();
      if (kindFilter) params.set('kind', kindFilter);
      if (catFilter) params.set('category', catFilter);
      if (fits) params.set('fits_tank', '1');
      if (beginner) params.set('beginner', '1');
      if (term && term.trim().length >= 2) params.set('q', term.trim());
      params.set('limit', '80');
      const data = await api(`/api/tanks/${tank.id}/livestock-recommendations?${params}`, {
        signal,
      });
      return data;
    },
    [tank.id]
  );

  const reload = (immediate = false) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const run = () => {
      if (abortRef.current) abortRef.current.abort();
      const ctl = new AbortController();
      abortRef.current = ctl;
      setLoading(true);
      setErr('');
      fetchResults(kind, category, fitsOnly, beginnerOnly, q, ctl.signal)
        .then((data) => {
          if (ctl.signal.aborted) return;
          setResults(data.results || []);
          setPhase(data.phase || null);
        })
        .catch((e) => {
          if (e?.name === 'AbortError') return;
          setErr(e.message || 'Could not load species.');
        })
        .finally(() => {
          if (!ctl.signal.aborted) setLoading(false);
        });
    };
    if (immediate) run();
    else debounceRef.current = setTimeout(run, 250);
  };

  useEffect(() => {
    reload(true);
  }, [kind, category, fitsOnly, beginnerOnly]);

  // Switching kinds wipes the category selection — a coral chip makes no
  // sense once the user flips to fish or inverts.
  useEffect(() => {
    setCategory('');
  }, [kind]);

  useEffect(() => {
    reload(false);
  }, [q]);

  const onAdded = async () => {
    setSelected(null);
    await onReload();
    reload(true);
  };

  // Quick-add path — no detail sheet, no qty stepper. Skips the "no"
  // verdict so the user has to read the reasons before forcing the add.
  const quickAdd = async (row) => {
    if (addingId) return;
    setQuickErr('');
    setAddingId(row.id);
    try {
      await api(`/api/tanks/${tank.id}/livestock`, {
        method: 'POST',
        body: {
          catalog_id: row.id,
          qty: 1,
          added_at: addedOn || localDateIso(),
        },
      });
      setAddedIds((prev) => {
        const next = new Set(prev);
        next.add(row.id);
        return next;
      });
      // Says the date too: the date control is at the top of the page,
      // out of sight once the keeper has scrolled the grid.
      setQuickMsg(`Added ${row.common_name}, dated ${dayLabel(addedOn || localDateIso())}.`);
      if (addedTimerRef.current) clearTimeout(addedTimerRef.current);
      addedTimerRef.current = setTimeout(() => {
        setQuickMsg('');
        setAddedIds((prev) => {
          const next = new Set(prev);
          next.delete(row.id);
          return next;
        });
      }, 2400);
      await onReload();
    } catch (e) {
      setQuickErr(e.message || 'Could not add to tank.');
    } finally {
      setAddingId(null);
    }
  };

  const onCardKeyDown = (e, row) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setSelected(row.id);
    }
  };

  const emptyMessage = (() => {
    if (loading) return null;
    if (err) return err;
    if (results.length > 0) return null;
    return catalogEmptyMessage(tank, { phase, fitsOnly });
  })();

  return (
    <div className="lb-page" aria-labelledby="lb-title">
      <header className="lb-page-header">
        <button
          type="button"
          aria-label={selected ? 'Back to species list' : `Back to ${backLabel.toLowerCase()}`}
          className="ts-back"
          onClick={() => (selected ? setSelected(null) : onClose())}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
          <span>{selected ? 'Browse' : backLabel}</span>
        </button>
        <div className="ts-page-title">
          <div className="lb-eyebrow">{selected ? 'Species detail' : 'Add livestock'}</div>
          <h2 id="lb-title" className="font-heading lb-title">
            {selected ? tank.name.toUpperCase() : 'Browse species'}
          </h2>
          {!selected && phase && (
            <div className="lb-phase">
              Phase: <strong>{phase}</strong>
            </div>
          )}
        </div>
        {!selected && (
          <div className={`lb-added-on${addedOn !== localDateIso() ? ' is-past' : ''}`}>
            <label>
              <span className="font-heading">Date added to the tank</span>
              <input
                type="date"
                max={localDateIso()}
                value={addedOn}
                onChange={(e) => setAddedOn(e.target.value || localDateIso())}
              />
            </label>
            {addedOn !== localDateIso() && (
              <>
                <span className="lb-added-on-note">
                  Everything you add here is dated {dayLabel(addedOn)}.
                </span>
                <button
                  type="button"
                  className="lb-added-on-today"
                  onClick={() => setAddedOn(localDateIso())}
                >
                  Today
                </button>
              </>
            )}
          </div>
        )}
      </header>

      {selected ? (
        <LivestockDetailSheet
          tank={tank}
          catalogId={selected}
          onAdded={onAdded}
          addedOn={addedOn}
          onAddedOnChange={setAddedOn}
        />
      ) : (
        <div className="lb-page-body">
          <input
            type="text"
            className="lb-search"
            placeholder="Search by name (clownfish, hammer, snail...)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            aria-label="Search species"
          />

          <div className="lb-tabs" role="tablist" aria-label="Species kind">
            {KIND_TABS.map((t) => (
              <button
                key={t.label}
                type="button"
                role="tab"
                aria-selected={kind === t.k}
                className={`lb-tab ${kind === t.k ? 'active' : ''}`}
                onClick={() => setKind(t.k)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {CATEGORY_OPTIONS[kind] && (
            <div className="lb-cats" role="group" aria-label="Category filter">
              <button
                type="button"
                className={`lb-cat ${category === '' ? 'active' : ''}`}
                onClick={() => setCategory('')}
              >
                All
              </button>
              {CATEGORY_OPTIONS[kind].map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`lb-cat ${category === c ? 'active' : ''}`}
                  onClick={() => setCategory(c)}
                >
                  {c}
                </button>
              ))}
            </div>
          )}

          <div className="lb-filters">
            <label className="lb-filter">
              <input
                type="checkbox"
                checked={fitsOnly}
                onChange={(e) => setFitsOnly(e.target.checked)}
              />
              <span>Fits my tank</span>
            </label>
            <label className="lb-filter">
              <input
                type="checkbox"
                checked={beginnerOnly}
                onChange={(e) => setBeginnerOnly(e.target.checked)}
              />
              <span>Beginner only</span>
            </label>
          </div>

          <div className="lb-body">
            {loading && (
              <div className="lb-grid">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="lb-card lb-skel" />
                ))}
              </div>
            )}
            {!loading && emptyMessage && (
              <div className="lb-empty">
                <p>{emptyMessage}</p>
                <button
                  type="button"
                  className="lb-custom-cta font-heading"
                  onClick={() => setCustomOpen(true)}
                >
                  + Add custom species
                </button>
              </div>
            )}
            {!loading && !emptyMessage && (
              <div className="lb-grid">
                {results.map((row) => {
                  const isAdded = addedIds.has(row.id);
                  const isAdding = addingId === row.id;
                  const blocked = row.verdict === 'no';
                  return (
                    <div
                      key={row.id}
                      role="button"
                      tabIndex={0}
                      className="lb-card"
                      style={{ borderColor: VERDICT_RING[row.verdict] || 'var(--surface-border)' }}
                      onClick={() => setSelected(row.id)}
                      onKeyDown={(e) => onCardKeyDown(e, row)}
                      aria-label={`${row.common_name} — verdict ${row.verdict}`}
                    >
                      <div className="lb-card-img">
                        {row.has_video ? (
                          <AutoVideo
                            src={`/api/livestock-catalog/${row.id}/video`}
                            poster={
                              row.image_url
                                ? `/api/livestock-image/${row.id}`
                                : `/api/livestock-catalog/${row.id}/video/poster`
                            }
                          />
                        ) : row.image_url ? (
                          // Serve stock artwork same-origin via the cache
                          // proxy — direct third-party hotlinks blank out
                          // under a full-grid burst, especially on mobile.
                          <img
                            src={`/api/livestock-image/${row.id}`}
                            alt=""
                            loading="lazy"
                            onError={(e) => {
                              e.currentTarget.style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="lb-card-img-fallback">
                            {row.kind === 'fish' ? '🐟' : row.kind === 'coral' ? '🪸' : '🦐'}
                          </div>
                        )}
                        <div
                          className={`lb-verdict-dot lb-verdict-${row.verdict}`}
                          title={`Rules verdict: ${row.verdict}`}
                        />
                        {row.category && (
                          <span className="lb-cat-chip" title={`Category: ${row.category}`}>
                            {row.category}
                          </span>
                        )}
                        {!blocked && (
                          <button
                            type="button"
                            className={`lb-quick-add ${isAdded ? 'is-added' : ''}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isAdded || isAdding) return;
                              quickAdd(row);
                            }}
                            disabled={isAdding}
                            aria-label={
                              isAdded
                                ? `Added ${row.common_name}, dated ${dayLabel(addedOn)}`
                                : `Add ${row.common_name} to tank, dated ${dayLabel(addedOn)}`
                            }
                            title={
                              isAdded
                                ? `Added, dated ${dayLabel(addedOn)}`
                                : `Add 1 to tank, dated ${dayLabel(addedOn)}`
                            }
                          >
                            {isAdded ? '✓' : isAdding ? '…' : '+'}
                          </button>
                        )}
                      </div>
                      <div className="lb-card-body">
                        <div className="lb-card-name">{row.common_name}</div>
                        <div className="lb-card-meta">
                          {row.care_level && <span>{row.care_level}</span>}
                          {row.min_tank_gal && (
                            <span>
                              {formatVolume(row.min_tank_gal, getDisplayUnits(tank).volume, {
                                decimals: 0,
                              })}
                              +
                            </span>
                          )}
                          {row.captive_bred && <span className="lb-cb">CB</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          {!loading && !emptyMessage && (
            <button
              type="button"
              className="lb-custom-link font-heading"
              onClick={() => setCustomOpen(true)}
            >
              Don't see your species? Add it manually
            </button>
          )}
          {quickMsg && (
            <div className="lb-quick-toast" role="status">
              {quickMsg}
            </div>
          )}
          {quickErr && (
            <div className="lb-quick-err" role="alert">
              {quickErr}
            </div>
          )}
          {customOpen && (
            <CustomLivestockForm
              tank={tank}
              addedOn={addedOn}
              onClose={() => setCustomOpen(false)}
              onAdded={async () => {
                setCustomOpen(false);
                await onReload();
                reload(true);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

// Free-text add for species not in the catalog (yet). Posts to the
// existing POST /api/tanks/:tankId/livestock without a catalog_id —
// the server already accepts that shape.
function CustomLivestockForm({ tank, addedOn: initialAddedOn, onClose, onAdded }) {
  const [commonName, setCommonName] = useState('');
  const [scientificName, setScientificName] = useState('');
  const [kind, setKind] = useState('fish');
  const [qty, setQty] = useState(1);
  const [addedOn, setAddedOn] = useState(initialAddedOn || localDateIso());
  const [notes, setNotes] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaPreviews, setMediaPreviews] = useState([]);
  // Opt-in (owner, 2026-10-01): shared to the catalog review queue only
  // when the keeper ticks the box.
  const [shareWithCatalog, setShareWithCatalog] = useState(false);
  const [extras, setExtras] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // AI photo-recognition assist. `aiStatus` is one of:
  //   'idle'    — no AI request fired yet (no photo, or already dismissed)
  //   'pending' — request in flight
  //   'done'    — suggestion ready (held on `aiSuggestion`)
  //   'failed'  — request errored (we stay silent; manual form still works)
  const [aiStatus, setAiStatus] = useState('idle');
  const [aiSuggestion, setAiSuggestion] = useState(null);
  // Stash the file we already asked about so we don't fire a second call
  // when the user picks more photos.
  const aiAskedFor = useRef(null);

  const onKindChange = (next) => {
    setKind(next);
    setExtras({});
  };
  const setExtra = (key, value) => setExtras((prev) => ({ ...prev, [key]: value }));

  const onPickMedia = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (!picked.length) return;
    const accepted = [];
    const previews = [];
    for (const f of picked) {
      const isImage = looksLikeImage(f);
      const isVideo = looksLikeVideo(f);
      if (!isImage && !isVideo) continue;
      if (isVideo && f.size > VIDEO_MAX_BYTES) {
        setErr(`${f.name || 'Video'} is too large (25 MB max).`);
        continue;
      }
      accepted.push(f);
      previews.push({
        kind: isImage ? 'image' : 'video',
        url: URL.createObjectURL(f),
        name: f.name || (isImage ? 'photo' : 'video'),
      });
    }
    if (!accepted.length) return;
    setMediaFiles((prev) => [...prev, ...accepted]);
    setMediaPreviews((prev) => [...prev, ...previews]);
    setErr('');
  };
  const removeMedia = (idx) => {
    setMediaPreviews((prev) => {
      const next = prev.slice();
      const [gone] = next.splice(idx, 1);
      if (gone?.url) {
        try {
          URL.revokeObjectURL(gone.url);
        } catch {
          /* already revoked */
        }
      }
      return next;
    });
    setMediaFiles((prev) => prev.slice(0, idx).concat(prev.slice(idx + 1)));
  };

  // Fire the AI ID request once the user attaches their first image. The
  // photo is uploaded raw — the server's normaliseImage handles HEIC and
  // re-encodes before the Vision call. We skip videos and re-uses
  // (aiAskedFor) so the request fires exactly once per upload session.
  useEffect(() => {
    const firstImage = mediaFiles.find((f) => looksLikeImage(f));
    if (!firstImage) {
      aiAskedFor.current = null;
      return;
    }
    if (aiAskedFor.current === firstImage) return;
    aiAskedFor.current = firstImage;
    setAiStatus('pending');
    setAiSuggestion(null);
    (async () => {
      try {
        const params = new URLSearchParams();
        if (commonName.trim()) params.set('name', commonName.trim());
        if (kind) params.set('kind', kind);
        const res = await fetch(`/api/livestock-id?${params}`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'content-type': firstImage.type || 'image/jpeg' },
          body: firstImage,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const j = await res.json();
        if (j.ok && j.suggestion) {
          setAiSuggestion(j.suggestion);
          setAiStatus('done');
        } else {
          setAiStatus('failed');
        }
      } catch {
        setAiStatus('failed');
      }
    })();
  }, [mediaFiles, commonName, kind]);

  // Apply the AI suggestion to the form. The user can edit anything
  // afterwards — this is a one-tap prefill, not a lock-in. We avoid
  // stomping fields the user has already typed into.
  const applyAiSuggestion = () => {
    if (!aiSuggestion) return;
    // No Coral on a freshwater tank's form (feedback #74): the select would
    // show Fish while a coral was saved.
    const suggestedKind =
      aiSuggestion.kind === 'coral' && !catalogFits(tank) ? null : aiSuggestion.kind;
    if (suggestedKind && !commonName.trim()) {
      // Apply kind first so KIND_EXTRAS_SCHEMA matches what we set.
      if (suggestedKind !== kind) {
        setKind(suggestedKind);
        setExtras({});
      }
    } else if (suggestedKind && suggestedKind !== kind) {
      setKind(suggestedKind);
      setExtras({});
    }
    if (aiSuggestion.common_name && !commonName.trim()) {
      setCommonName(aiSuggestion.common_name);
    }
    if (aiSuggestion.scientific_name && !scientificName.trim()) {
      setScientificName(aiSuggestion.scientific_name);
    }
    const schema = customSpeciesFields(KIND_EXTRAS_SCHEMA[suggestedKind || kind] || [], tank);
    const nextExtras = {};
    for (const field of schema) {
      const v = aiSuggestion[field.key];
      if (v == null || v === '') continue;
      // A value the select cannot show (an anemone on a freshwater form)
      // would be saved unseen.
      if (field.options && !field.options.includes(String(v))) continue;
      nextExtras[field.key] = String(catalogValueShown(v, field.unitKind, getDisplayUnits(tank)));
    }
    if (Object.keys(nextExtras).length) {
      setExtras((prev) => ({ ...nextExtras, ...prev }));
    }
    setAiStatus('idle');
  };

  // Set once the create POST succeeds. A retry after a partial upload
  // failure must NOT re-run the create — the resident already exists,
  // and re-submitting used to insert a duplicate. With the id pinned,
  // retries skip straight to re-uploading whatever is still queued.
  const [createdId, setCreatedId] = useState(null);

  const submit = async () => {
    const name = commonName.trim();
    if (!name) {
      setErr('Common name is required.');
      return;
    }
    setBusy(true);
    setErr('');
    // Combine the structured kind-specific bullets with the user's
    // free notes into a single notes blob. Bullets render in a
    // monospace-ish block in the resident edit sheet; free notes sit
    // below.
    const extrasBlock = formatExtrasBlock(extras, kind, getDisplayUnits(tank));
    const freeNotes = notes.trim();
    const combined = [extrasBlock, freeNotes].filter(Boolean).join('\n\n').slice(0, 500);
    try {
      let residentId = createdId;
      if (!residentId) {
        const created = await api(`/api/tanks/${tank.id}/livestock`, {
          method: 'POST',
          body: {
            common_name: name.slice(0, 100),
            scientific_name: scientificName.trim().slice(0, 120) || null,
            kind,
            qty,
            added_at: addedOn || localDateIso(),
            notes: combined || null,
          },
        });
        residentId = created?.id || null;
        setCreatedId(residentId);
      }
      // Media uploads chain after the create so we have the new
      // livestock id to attach against. Each file is routed via the
      // shared uploader — images get the HEIC-safe path, videos get
      // poster extraction + Range-streamable storage. If any upload
      // fails, the resident is still in but we surface the failures
      // so the user knows to retry — silently closing the form would
      // look like success when photos / clips are missing.
      const failedIdx = new Set();
      const failedNames = [];
      if (residentId && mediaFiles.length) {
        for (let i = 0; i < mediaFiles.length; i++) {
          const f = mediaFiles[i];
          try {
            await uploadResidentMedia({
              tankId: tank.id,
              residentId,
              file: f,
              shareWithCatalog,
            });
          } catch {
            failedIdx.add(i);
            failedNames.push(f.name || `file ${i + 1}`);
          }
        }
      }
      if (failedIdx.size) {
        // Drop the files that DID land (revoking their previews) so
        // the retry only re-sends the failures, then keep the sheet
        // open with the button live. The pinned createdId above
        // guarantees the retry can't duplicate the resident.
        for (let i = 0; i < mediaPreviews.length; i++) {
          if (!failedIdx.has(i) && mediaPreviews[i]?.url) {
            try {
              URL.revokeObjectURL(mediaPreviews[i].url);
            } catch {
              /* already revoked */
            }
          }
        }
        setMediaPreviews((prev) => prev.filter((_, i) => failedIdx.has(i)));
        setMediaFiles((prev) => prev.filter((_, i) => failedIdx.has(i)));
        const n = failedIdx.size;
        setErr(
          `Resident added, but ${n} ${n === 1 ? 'upload' : 'uploads'} failed (${failedNames.join(', ')}). Tap "Retry uploads" to send ${n === 1 ? 'it' : 'them'} again.`
        );
        setBusy(false);
        return;
      }
      await onAdded();
    } catch (e) {
      setErr(e.message || 'Could not add.');
      setBusy(false);
    }
  };

  // Revoke any blob URLs the user attached but didn't complete with —
  // closing the sheet via Cancel / backdrop click previously leaked
  // each ObjectURL until the tab closed. The ref pattern lets the
  // unmount cleanup see the LATEST previews array, not the one
  // captured at mount time.
  const previewsRef = useRef(mediaPreviews);
  previewsRef.current = mediaPreviews;
  useEffect(() => {
    return () => {
      for (const m of previewsRef.current) {
        if (m?.url) {
          try {
            URL.revokeObjectURL(m.url);
          } catch {
            /* already revoked */
          }
        }
      }
    };
  }, []);

  return (
    <div
      className="lb-custom-overlay"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="lb-custom-sheet">
        <header className="lb-custom-head">
          <h3 className="font-heading">Add a custom species</h3>
          <button
            type="button"
            className="lb-custom-close"
            onClick={onClose}
            aria-label="Close"
            disabled={busy}
          >
            ×
          </button>
        </header>
        <p className="lb-custom-hint">{customSpeciesHint(tank)}</p>
        {aiStatus === 'pending' && (
          <div className="lb-ai-pill is-pending" role="status">
            <span className="lb-ai-spinner" aria-hidden="true" />
            Sensei is looking at your photo…
          </div>
        )}
        {aiStatus === 'done' && aiSuggestion && (
          <div className="lb-ai-pill is-done" role="region" aria-label="Sensei suggestion">
            <div className="lb-ai-pill-body">
              <strong>
                {aiSuggestion.confidence < 0.5 ? 'Sensei is unsure — looks like' : 'Sensei sees'}
                :{' '}
              </strong>
              {aiSuggestion.common_name || aiSuggestion.scientific_name || aiSuggestion.kind || '—'}
              {aiSuggestion.scientific_name && aiSuggestion.common_name && (
                <em> ({aiSuggestion.scientific_name})</em>
              )}
              {aiSuggestion.category && <span className="lb-ai-cat">{aiSuggestion.category}</span>}
              {aiSuggestion.note && <div className="lb-ai-note">{aiSuggestion.note}</div>}
            </div>
            <div className="lb-ai-pill-actions">
              <button
                type="button"
                className="lb-ai-apply font-heading"
                onClick={applyAiSuggestion}
              >
                Apply
              </button>
              <button
                type="button"
                className="lb-ai-dismiss"
                onClick={() => setAiStatus('idle')}
                aria-label="Dismiss suggestion"
              >
                ×
              </button>
            </div>
          </div>
        )}
        <label className="lb-custom-label font-heading">
          Common name *
          <input
            type="text"
            value={commonName}
            onChange={(e) => setCommonName(e.target.value)}
            placeholder={catalogFits(tank) ? 'e.g. Tail-spot blenny' : 'e.g. Neon tetra'}
            maxLength={100}
            autoFocus
            className="lb-custom-input"
          />
        </label>
        <label className="lb-custom-label font-heading">
          Scientific name
          <input
            type="text"
            value={scientificName}
            onChange={(e) => setScientificName(e.target.value)}
            placeholder={
              catalogFits(tank) ? 'e.g. Ecsenius stigmatura' : 'e.g. Paracheirodon innesi'
            }
            maxLength={120}
            className="lb-custom-input"
          />
        </label>
        <label className="lb-custom-label font-heading">
          Kind
          <select
            value={kind}
            onChange={(e) => onKindChange(e.target.value)}
            className="lb-custom-input"
          >
            <option value="fish">Fish</option>
            {catalogFits(tank) && <option value="coral">Coral</option>}
            <option value="invert">Invert</option>
          </select>
        </label>
        <div className="lb-custom-extras">
          <div className="lb-custom-extras-eyebrow font-heading">
            {kind === 'fish'
              ? 'Fish details'
              : kind === 'coral'
                ? 'Coral details'
                : 'Invert details'}
            <span className="lb-custom-extras-hint"> (optional)</span>
          </div>
          <div className="lb-custom-extras-grid">
            {customSpeciesFields(KIND_EXTRAS_SCHEMA[kind] || [], tank).map((field) => (
              <label key={field.key} className="lb-custom-label font-heading">
                {catalogFieldLabel(field.label, field.unitKind, getDisplayUnits(tank))}
                {field.options ? (
                  <select
                    value={extras[field.key] || ''}
                    onChange={(e) => setExtra(field.key, e.target.value)}
                    className="lb-custom-input"
                  >
                    <option value="">—</option>
                    {field.options.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.type === 'number' ? 'number' : 'text'}
                    inputMode={field.type === 'number' ? 'numeric' : undefined}
                    min={field.type === 'number' ? 1 : undefined}
                    value={extras[field.key] || ''}
                    onChange={(e) => setExtra(field.key, e.target.value)}
                    className="lb-custom-input"
                    placeholder={field.type === 'number' ? '20' : ''}
                  />
                )}
              </label>
            ))}
          </div>
        </div>
        <label className="lb-custom-label font-heading">
          Quantity
          <input
            type="number"
            inputMode="numeric"
            min="1"
            max="9999"
            value={qty}
            onChange={(e) => setQty(Math.max(1, Math.min(9999, Number(e.target.value) || 1)))}
            className="lb-custom-input"
          />
        </label>
        <label className="lb-custom-label font-heading">
          Date added to the tank
          <input
            type="date"
            max={localDateIso()}
            value={addedOn}
            onChange={(e) => setAddedOn(e.target.value)}
            className="lb-custom-input"
          />
        </label>
        <div className="lb-custom-label font-heading">
          Photos &amp; video
          {mediaPreviews.length > 0 && (
            <div className="lb-custom-media-grid">
              {mediaPreviews.map((m, i) => (
                <div key={`${m.url}-${i}`} className="lb-custom-media-item">
                  {m.kind === 'video' ? (
                    <video src={m.url} muted loop playsInline autoPlay preload="metadata" />
                  ) : (
                    <img src={m.url} alt={m.name} />
                  )}
                  <button
                    type="button"
                    className="lb-custom-media-x"
                    onClick={() => removeMedia(i)}
                    disabled={busy}
                    aria-label={`Remove ${m.name}`}
                  >
                    ×
                  </button>
                  {m.kind === 'video' && <span className="lb-custom-media-badge">video</span>}
                </div>
              ))}
            </div>
          )}
          <label className="lb-custom-photo-btn font-heading">
            + Add photo or video
            <input
              type="file"
              accept={MEDIA_ACCEPT}
              multiple
              style={{ display: 'none' }}
              onChange={onPickMedia}
              disabled={busy}
            />
          </label>
        </div>
        {mediaFiles.length > 0 && (
          <label className="lb-custom-share">
            <input
              type="checkbox"
              checked={shareWithCatalog}
              onChange={(e) => setShareWithCatalog(e.target.checked)}
              disabled={busy}
            />
            <span>
              Share these with the catalog so other keepers can see them (admin reviews before
              promoting).
            </span>
          </label>
        )}
        <label className="lb-custom-label font-heading">
          Notes
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything Sensei should know about this one?"
            maxLength={500}
            className="lb-custom-input"
            rows={3}
          />
        </label>
        {err && (
          <p className="lb-custom-err" role="alert">
            {err}
          </p>
        )}
        <div className="lb-custom-actions">
          <button
            type="button"
            className="lb-custom-cancel font-heading"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="lb-custom-save font-heading"
            onClick={submit}
            disabled={busy || !commonName.trim()}
          >
            {busy
              ? createdId
                ? 'Uploading…'
                : 'Adding…'
              : createdId
                ? 'Retry uploads'
                : 'Add to tank'}
          </button>
        </div>
      </div>
    </div>
  );
}
